import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import API from "../../../services/api";
import { Toast } from "../../../shared/Toast";
import { BrandsTypes, formatAmount } from "../../../utils/constants";
import Select from "react-select";
import { CSVLink } from "react-csv";
import BrandWiseDetails from "./BrandWiseDetails";

const ExclusiveW2NCustomers = () => {
  const [year, setYear] = useState(2025);
  const [selectedBrandType, setSelectedBrandType] = useState(2);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showBrandDetails, setShowBrandDetails] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const result = await API({
          url: "/review-reports/exclusiveW2NCustomers",
          method: "POST",
          data: { year, brand_type: selectedBrandType },
        });
        if (result.status === "SUCCESS") {
          setData(result.data?.[0] || null);
        } else {
          Toast("error", result.message ?? "Failed To Fetch Data");
        }
      } catch {
        Toast("error", "An error occurred while fetching data");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [year, selectedBrandType]);

  const calculateAvgDaysPerMonth = (activeDays) => {
    if (!activeDays) return 0;
    return (parseFloat(activeDays) / 12).toFixed(2);
  };

  const calculateAvgRevenue = (totalRevenue, activeDays) => {
    if (!totalRevenue || !activeDays) return 0;
    return (parseFloat(totalRevenue) / parseFloat(activeDays)).toFixed(2);
  };

  const generateCSVData = () => {
    if (!data) return [];
    
    const headers = [
      ["W2N Exclusive Accounts Report"],
      [`Year: ${year}`],
      [`Brand Type: ${BrandsTypes?.find(b => +b.value === +selectedBrandType)?.label || 'All'}`],
      [],
      ["Summary Stats"],
      ["Metric", "Value"],
      ["Total Assigned Accounts", data.total_assigned_brands || 0],
      ["W2N Deployed Accounts", data.total_w2n_brands || 0],
      ["Exclusive Eenadu deployed Accounts", data.eenadu_only_brands || 0],
      ["Common with Eenadu", data.common_brands_count || 0],
      ["W2N Insertions", data.w2n_insertions || 0],
      ["Eenadu Insertions", data.eenadu_insertions || 0],
      [],
      ["Detailed Data"],
      ["Category", "No. of Accounts", `${year} Revenues`, "Av days of activity/ month", "Av revenues/ day of activity"]
    ];
    
    const rows = [
      [`W2N ${year}`, data.w2n_only_brand_count || 0, data.w2n_only_total_revenue || 0, calculateAvgDaysPerMonth(data.w2n_only_active_days), calculateAvgRevenue(data.w2n_only_total_revenue, data.w2n_only_active_days)],
      [`W2N ${year + 1} plan`, "", "", "", ""]
    ];
    
    return [...headers, ...rows];
  };

  return (
    <div className="p-4 max-w-full">
      <div className="flex flex-wrap gap-6 items-end rounded px-4 py-2 bg-white mb-6">
        <div className="flex items-center mb-4 sm:mb-0">
          <label className="mr-2 font-semibold text-sm md:text-base">Select Year:</label>
          <DatePicker
            selected={new Date(year, 0, 1)}
            onChange={(date) => setYear(date.getFullYear())}
            showYearPicker
            dateFormat="yyyy"
            className="border border-gray-300 rounded text-center px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
          />
        </div>
        <div className="flex flex-col mb-4 min-w-[180px]">
          <Select
            options={BrandsTypes}
            value={BrandsTypes?.find((b) => +b.value === +selectedBrandType) ?? null}
            onChange={(item) => setSelectedBrandType(item?.value ?? null)}
            placeholder="Select Brand Type"
            isClearable
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-8">
          <div className="text-gray-600">Loading...</div>
        </div>
      ) : data ? (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">W2N Exclusive Accounts</h2>
            <CSVLink 
              data={generateCSVData()} 
              filename={`w2n_exclusive_accounts_${year}.csv`} 
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-4 py-2 rounded text-sm"
            >
              Download CSV
            </CSVLink>
          </div>

          <div className="mb-6 border border-gray-800">
            <div className="grid grid-cols-1 gap-0">
              <div className="flex border-b border-gray-800">
                <div className="w-1/2 px-2 py-1 font-semibold bg-yellow-100 border-r border-gray-800">W2N Deployed Accounts:</div>
                <div className="w-1/2 px-2 py-1 text-center" title={data.total_w2n_brands || 0}>{data.total_assigned_brands || 0}</div>
              </div>
              <div className="flex border-b border-gray-800">
                <div className="w-1/2 px-2 py-1 font-semibold bg-yellow-100 border-r border-gray-800">Eenadu deployed Accounts:</div>
                <div className="w-1/2 px-2 py-1 text-center" title={data.eenadu_only_brands || 0}>{data.eenadu_all || 0}</div>
              </div>
              <div className="flex border-b border-gray-800">
                <div className="w-1/2 px-2 py-1 font-semibold bg-yellow-100 border-r border-gray-800">W2N Insertions:</div>
                <div className="w-1/2 px-2 py-1 text-center">{data.w2n_insertions || 0}</div>
              </div>
              <div className="flex border-b border-gray-800">
                <div className="w-1/2 px-2 py-1 font-semibold bg-yellow-100 border-r border-gray-800">Eenadu Insertions:</div>
                <div className="w-1/2 px-2 py-1 text-center">{data.eenadu_insertions || 0}</div>
              </div>
              <div className="flex border-b border-gray-800">
                <div className="w-1/2 px-2 py-1 font-semibold bg-yellow-100 border-r border-gray-800">Exclusive W2N Deployed:</div>
                <div className="w-1/2 px-2 py-1 text-center" title={data.total_w2n_brands || 0}>{data.total_assigned_brands - data.eenadu_all || 0}</div>
              </div>
              <div className="flex border-b border-gray-800">
                <div className="w-1/2 px-2 py-1 font-semibold bg-yellow-100 border-r border-gray-800">Common with Eenadu:</div>
                <div className="w-1/2 px-2 py-1 text-center" title={data.common_brands_count || 0}>{data.common_brands_count || 0}</div>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-800 text-sm">
              <thead>
                <tr className="bg-yellow-200">
                  <th className="border border-gray-800 px-4 py-2 text-left"></th>
                  <th className="border border-gray-800 px-4 py-2 text-center">No. of Accounts</th>
                  <th className="border border-gray-800 px-4 py-2 text-center">{year} Revenues</th>
                  <th className="border border-gray-800 px-4 py-2 text-center">Av days of activity/ month</th>
                  <th className="border border-gray-800 px-4 py-2 text-center">Av revenues/ day of activity</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-yellow-50">
                  <td className="border border-gray-800 px-4 py-2 font-semibold">W2N {year} Exclusive</td>
                  <td className="border border-gray-800 px-4 py-2 text-center text-blue-500 underline cursor-pointer" title={data.w2n_only_brand_count || 0} onClick={() => setShowBrandDetails(true)}>{data.w2n_only_brand_count || 0}</td>
                  <td className="border border-gray-800 px-4 py-2 text-center" title={data.w2n_only_total_revenue || 0}>{formatAmount(data.w2n_only_total_revenue || 0)}</td>
                  <td className="border border-gray-800 px-4 py-2 text-center" title={calculateAvgDaysPerMonth(data.w2n_only_active_days)}>{calculateAvgDaysPerMonth(data.w2n_only_active_days)}</td>
                  <td className="border border-gray-800 px-4 py-2 text-center" title={calculateAvgRevenue(data.w2n_only_total_revenue, data.w2n_only_active_days)}>{formatAmount(calculateAvgRevenue(data.w2n_only_total_revenue, data.w2n_only_active_days))}</td>
                </tr>
                <tr className="bg-gray-100">
                  <td className="border border-gray-800 px-4 py-2 font-semibold">W2N {year + 1} plan</td>
                  <td className="border border-gray-800 px-4 py-2 text-center bg-gray-300"></td>
                  <td className="border border-gray-800 px-4 py-2 text-center bg-gray-300"></td>
                  <td className="border border-gray-800 px-4 py-2 text-center bg-gray-300"></td>
                  <td className="border border-gray-800 px-4 py-2 text-center bg-gray-300"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="flex justify-center items-center py-8">
          <div className="text-gray-600">No data available</div>
        </div>
      )}
      <BrandWiseDetails year={year} brand_type={selectedBrandType} url="/review-reports/exclusiveBrandWiseDetails" open={showBrandDetails} onClose={() => setShowBrandDetails(false)} key={showBrandDetails}/>
    </div>
  );
};

export default ExclusiveW2NCustomers;
